/**
 * Whether this page is running inside the Windows shell instead of a browser.
 *
 * The Electron shell of Fase 6 loads this very application from the same URL
 * a browser does, so the page cannot tell the two apart on its own. The shell
 * marks itself on `window.discordo` — a bridge with exactly three entries —
 * and that mark is all the app knows about the desktop.
 *
 * Everything that reads this must leave the browser untouched: no mark, no
 * change. The app runs in production in browsers, and the desktop shell is the
 * new thing; the burden of being harmless is on the new thing.
 */

export interface DesktopShellBridge {
  readonly isDesktop: true;
  readonly retry: () => void;
  /** The global push-to-talk key going down and coming up. */
  readonly onPushToTalk: (listener: (talking: boolean) => void) => () => void;
  /** Tells the shell whether the person is in a voice channel right now. */
  readonly setInCall: (inCall: boolean) => void;
}

declare global {
  interface Window {
    readonly discordo?: Partial<DesktopShellBridge>;
  }
}

/** The shape this check needs, so a test can hand it one without a browser. */
interface MarkedScope {
  readonly discordo?: {
    readonly isDesktop?: unknown;
    readonly setInCall?: (inCall: boolean) => void;
    readonly onPushToTalk?: (listener: (talking: boolean) => void) => () => void;
  };
}

/** The real `window`, or nothing when this runs without one. */
function currentScope(): MarkedScope | undefined {
  return typeof window === 'undefined' ? undefined : window;
}

/**
 * The mark has to be exactly `true`.
 *
 * `window.discordo` could plausibly exist for some other reason on a page
 * served to a browser, and a truthy-enough value is not a reason to start
 * showing controls that only the shell can honour.
 */
export function isDesktopShell(scope: MarkedScope | undefined = currentScope()): boolean {
  return scope?.discordo?.isDesktop === true;
}

/**
 * Tells the shell that a voice call started or ended.
 *
 * §7 of the Fase 6 design: the shell fetches its own updates in the background
 * and never during a call — and the page is the only side that knows a call is
 * happening. This is the same fact `appVersion.ts` consults before reloading
 * for a new deploy, so one signal governs both layers instead of each guessing
 * separately.
 *
 * In a browser there is no shell and this does nothing, which is the whole
 * contract: the app runs in production in browsers, and the burden of being
 * harmless is on the desktop, not on them. An installed shell older than this
 * page has no `setInCall` at all — the page updates on every deploy, the shell
 * almost never — so the call is made only if it is there.
 */
export function reportCallState(
  inCall: boolean,
  scope: MarkedScope | undefined = currentScope(),
): void {
  const setInCall = scope?.discordo?.setInCall;
  if (typeof setInCall !== 'function') return;

  try {
    setInCall(inCall);
  } catch {
    // The bridge belongs to another process. Losing an update notice is not a
    // reason to take down the call that was being reported.
  }
}

/**
 * Listens to the shell's global push-to-talk key. Returns how to stop.
 *
 * §5.2 of the Fase 6 design: the key is registered with Windows, so it fires
 * with the window minimised or behind something else — which is the whole
 * point, since a focused window needs no help from the shell. The shell works
 * out pressing and releasing from the auto-repeat and says which of the two
 * just happened; this is the page picking that up.
 *
 * Everything here is written for the two cases where there is nothing to
 * listen to. A browser has no bridge, and an installed shell older than this
 * page has no `onPushToTalk` — the page updates on every deploy, the shell
 * almost never — so both get a subscription that does nothing and an
 * unsubscribe that is safe to call. The voice panel is worth more than the
 * shortcut it is trying to wire up.
 */
export function subscribeToPushToTalk(
  listener: (talking: boolean) => void,
  scope: MarkedScope | undefined = currentScope(),
): () => void {
  const onPushToTalk = scope?.discordo?.onPushToTalk;
  if (typeof onPushToTalk !== 'function') return () => undefined;

  try {
    // Narrowed on this side as well as on the bridge's: what crosses decides
    // whether a microphone opens, and "truthy" is not an answer to "is the
    // key down right now?".
    const stop = onPushToTalk((talking) => {
      listener(talking === true);
    });

    return typeof stop === 'function' ? stop : () => undefined;
  } catch {
    // The bridge belongs to another process. Losing the shortcut is not a
    // reason to take down the call it was meant to serve.
    return () => undefined;
  }
}
